import { api } from '@/lib/api';
/**
 * 우측 패널 상단 — 사전 정의 테마 카드 그리드.
 * 클릭하면 slide.design 일괄 교체 (POST /api/slides/:id/design).
 */
import { SLIDE_THEMES, type SlideTheme, matchTheme } from '@/lib/slides/themes';
import type { SlideDesign } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

interface Props {
  slideId: string;
  currentDesign: SlideDesign;
}

export function ThemeGallery({ slideId, currentDesign }: Props) {
  const qc = useQueryClient();
  const active = matchTheme(currentDesign);

  const setDesignMut = useMutation({
    mutationFn: (theme: SlideTheme) => api.slideSetDesign(slideId, { design: theme.design }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slide', slideId] }),
  });

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-foreground-muted mb-3">테마</div>
      <div className="grid grid-cols-2 gap-2">
        {SLIDE_THEMES.map((t) => {
          const isActive = active?.id === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setDesignMut.mutate(t)}
              disabled={setDesignMut.isPending}
              className={`relative rounded-md border-2 transition-colors overflow-hidden text-left ${
                isActive ? 'border-accent' : 'border-border hover:border-foreground-muted'
              }`}
              title={t.description}
            >
              <div
                className="h-16 px-2 py-1.5 flex flex-col justify-between"
                style={{
                  background: t.preview.bg,
                  color: t.preview.text,
                  fontFamily: t.design.fonts.display,
                }}
              >
                <span className="text-[10px] font-bold leading-tight">제목</span>
                <span
                  className="text-[8px] self-end"
                  style={{ color: t.preview.accent, fontWeight: 700 }}
                >
                  ●
                </span>
              </div>
              <div className="px-2 py-1 bg-background flex items-center gap-1">
                <span className="text-[11px] font-medium truncate flex-1">{t.name}</span>
                {isActive && <Check className="size-3 text-accent" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
